import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { useStreams } from "../hooks/useStreams";
import { Loader2 } from "lucide-react";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { fetchGlossary, saveGlossary, parseTerms, validateTerms, MAX_TERMS } from "../utils/glossary";

const Glossary: React.FC = () => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);

  const [streamName, setStreamName] = useState("");
  const [text, setText] = useState("");
  const [saved, setSaved] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const { selected } = useStreams();
  useEffect(() => {
    if (selected) setStreamName(selected.name);
  }, [selected]);

  useEffect(() => {
    if (!user || !streamName) return;
    fetchGlossary(streamName, user.uid)
      .then((g) => {
        setText(g.terms.join("\n"));
        setSaved(g.terms);
        setUpdatedAt(g.updatedAt);
      })
      .catch(() => setStatus("Could not load your glossary. Please try again in a moment."))
      .finally(() => setReady(true));
  }, [user, streamName]);

  const terms = useMemo(() => parseTerms(text), [text]);
  const problem = validateTerms(terms);
  const changed = terms.join("\n") !== saved.join("\n");

  const onSave = async () => {
    if (!user || problem) return;
    setSaving(true);
    setStatus(null);
    try {
      await saveGlossary(streamName, user.uid, terms);
      setSaved(terms);
      setText(terms.join("\n"));
      setUpdatedAt(new Date().toISOString());
      setStatus("Saved.");
    } catch {
      setStatus("Could not save. Start your stream from the app once, then try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="glossary" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <header>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Glossary</h1>
              <p className="text-slate-600 mt-1 text-lg">Names, products and technical words that captions often get wrong.</p>
            </header>

            <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" aria-labelledby="glossary-heading">
              <h2 id="glossary-heading" className="text-xl font-bold text-slate-900">Terms for {streamName || "your stream"}</h2>
              <p className="text-slate-600 text-sm mt-1">
                Add one term per line, for example speaker names, company and product names, or acronyms. Your assistants and tools that read your stream (such as the MCP server) use this list to correct the transcript.
              </p>

              {!ready && streamName ? (
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mt-5" />
              ) : (
                <div className="mt-5 space-y-4">
                  <label htmlFor="glossary-terms" className="font-bold text-slate-900 block">Terms</label>
                  <textarea
                    id="glossary-terms"
                    rows={12}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3 font-mono text-sm"
                    placeholder={"Saamer Mansoor\nWhisperKit\nSignalR"}
                    value={text}
                    onChange={(e) => { setText(e.target.value); setStatus(null); }}
                    disabled={!streamName}
                  />
                  <p className={`text-sm ${problem ? "text-red-700" : "text-slate-600"}`} role={problem ? "alert" : undefined}>
                    {problem ?? `${terms.length} of ${MAX_TERMS} terms. Duplicates and blank lines are removed when you save.`}
                  </p>
                  {updatedAt && (
                    <p className="text-slate-700 text-sm">
                      Last saved <strong>{new Date(updatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</strong>.
                    </p>
                  )}
                  <div className="flex items-center gap-4">
                    <button onClick={onSave} disabled={saving || !streamName || !!problem || !changed} className="btn-primary">
                      {saving ? "Saving..." : "Save"}
                    </button>
                    {status && <span className="text-sm text-slate-700" role="status">{status}</span>}
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Glossary;
